import { Tooltip as RadixTooltip } from "radix-ui";
import { type ReactNode, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface TooltipProps {
	label: ReactNode;
	side?: "top" | "right" | "bottom" | "left";
	align?: "start" | "center" | "end";
	/** Задержка перед показом. 200ms — как в DS: подсказка не мигает при проезде мышью. */
	delayDuration?: number;
	/** Управляемое состояние — нужно `Hint`, чтобы подсказка открывалась по тапу. */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	children: ReactNode;
	className?: string;
}

/**
 * Чёрная плашка r6, 13px Geist Mono, отступ 2px от триггера.
 *
 * Провайдер вложен внутрь компонента намеренно: тултип обязан работать в чужом
 * сервисе, куда встраивается AccountMenu, где нашего корневого провайдера нет.
 * Вложенные провайдеры Radix безопасны.
 */
export function Tooltip({
	label,
	side = "top",
	align = "center",
	delayDuration = 200,
	open,
	onOpenChange,
	children,
	className,
}: TooltipProps) {
	return (
		<RadixTooltip.Provider delayDuration={delayDuration}>
			<RadixTooltip.Root
				{...(open !== undefined ? { open } : {})}
				{...(onOpenChange ? { onOpenChange } : {})}
			>
				<RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
				<RadixTooltip.Portal>
					<RadixTooltip.Content
						side={side}
						align={align}
						sideOffset={2}
						collisionPadding={8}
						// Тап вне подсказки закрывает её: на телефоне уводить палец
						// некуда, а `mouseleave` там не наступает никогда.
						onPointerDownOutside={() => onOpenChange?.(false)}
						className={cn(
							// Самый верхний слой: подсказка объясняет то, что уже
							// открыто поверх остального (`z-140` > поповеры `z-130`).
							// 280px — предел читаемости строки, но на узком экране предел
							// задаёт экран: подсказка не имеет права уехать за край.
							"z-140 max-w-[min(280px,var(--radix-tooltip-content-available-width))]",
							"rounded-sm bg-ink px-2 py-1 text-on-ink",
							"t-num-xs whitespace-pre-line",
							"data-[state=delayed-open]:[animation:iec-reveal_var(--dur-react)_var(--ease-elegant)]",
							className,
						)}
					>
						{label}
					</RadixTooltip.Content>
				</RadixTooltip.Portal>
			</RadixTooltip.Root>
		</RadixTooltip.Provider>
	);
}

/** Общий провайдер приложения: группирует задержки соседних тултипов. */
export const TooltipProvider = RadixTooltip.Provider;

export interface HintProps {
	/** Текст подсказки. */
	label: ReactNode;
	/**
	 * Доступное имя самой кнопки-вопроса («Подробнее»). Обязательно: подсказка
	 * открывается по наведению и по нажатию, а скринридер объявляет только это имя.
	 */
	triggerLabel: string;
	side?: TooltipProps["side"];
	className?: string;
}

/**
 * Знак вопроса 16px рядом с заголовком — самый частый элемент витрины.
 * Это кнопка, а не span: подсказка должна открываться с клавиатуры.
 *
 * Состояние управляемое, потому что у Radix-тултипа тач-триггер — долгое
 * нажатие, и обычный тап не делал ничего (замечание владельца 22.08.2026).
 *
 * ПОЧЕМУ ЗДЕСЬ НЕ ПРОСТО `!previous`. Radix вешает на триггер свои
 * обработчики, и на тач-экране они срабатывают ВОКРУГ нашего клика:
 *
 *   1. `onPointerDown` — закрывает подсказку, если она открыта;
 *   2. `onFocus` — открывает её, если «палец нажат» уже сброшен. У мыши флаг
 *      живёт до `pointerup` и фокус приходит внутри нажатия, поэтому ветка
 *      молчит; на тач-экране фокус приезжает ПОСЛЕ `pointerup`, флага уже нет,
 *      и подсказка открывается;
 *   3. `onClick` — закрывает.
 *
 * Отсюда двойной тап: первый тап открывал подсказку шагом 2, а наш
 * `!previous` шагом 3 её закрывал. Второй тап фокуса уже не порождал, и
 * подсказка наконец открывалась.
 *
 * Поэтому переключается не текущее значение, а то, каким оно было ДО начала
 * нажатия, — его и знает только наш `onPointerDown`, который идёт первым.
 * `preventDefault` на клике отменяет закрытие Radix (шаг 3): составные
 * обработчики Radix пропускают своё действие на отменённом событии.
 */
export function Hint({ label, triggerLabel, side, className }: HintProps) {
	const [open, setOpen] = useState(false);
	const openAtPress = useRef<boolean | null>(null);

	return (
		<Tooltip label={label} side={side} open={open} onOpenChange={setOpen}>
			<button
				type="button"
				aria-label={triggerLabel}
				aria-expanded={open}
				onPointerDown={() => {
					openAtPress.current = open;
				}}
				onClick={(event) => {
					event.preventDefault();
					// `null` — активация с клавиатуры: нажатия указателем не было,
					// и текущее значение здесь достоверно.
					const wasOpen = openAtPress.current ?? open;
					openAtPress.current = null;
					setOpen(!wasOpen);
				}}
				className={cn(
					"iec-press-glyph tap-target inline-flex items-center text-fg-muted",
					"transition-colors duration-150 ease-smooth hover:text-fg",
					"focus-ring",
					className,
				)}
			>
				<Icon name="CircleHelp" size={16} />
			</button>
		</Tooltip>
	);
}
